/**
 * Pure view-state helpers for "fetch a resource by id" pages (e.g. UserEdit).
 * No React import — keep this dependency-light so it unit-tests in plain
 * node vitest and stays trivially reusable by any query-driven detail page.
 */
import { RequestError } from './request-error';

export type ResourceViewState = 'loading' | 'notFound' | 'error' | 'ready';

/**
 * Collapses a TanStack Query result into one of four render states. A 404
 * (deleted/other-tenant resource) gets its own "notFound" state distinct from
 * a transient/server "error", and a disabled query with no data (missing id
 * param) also lands on "notFound" rather than hanging forever on the old
 * `isLoading || !data` check (TanStack v5 reports isLoading=false there).
 */
export function resourceViewState(q: {
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  data: unknown;
}): ResourceViewState {
  if (q.isLoading) return 'loading';
  if (q.isError) {
    return q.error instanceof RequestError && q.error.status === 404 ? 'notFound' : 'error';
  }
  return q.data ? 'ready' : 'notFound';
}

/**
 * TanStack Query `retry` predicate: don't burn retries on a definitive 404
 * before showing the not-found card, but keep the default up-to-3-attempts
 * behavior for transient/server errors.
 */
export function retryUnlessNotFound(failureCount: number, error: unknown): boolean {
  return !(error instanceof RequestError && error.status === 404) && failureCount < 3;
}
